import DoughnutChart from '@c/charts/DoughnutChart';
import EmptyState from '@c/dashboard/EmptyState';
import { ReviewSummary } from '@c/dashboard/pages/RatingsPage';
import {
  DEFAULT_SHIPPING_POLICY,
} from '@c/dashboard/pages/ShopSettingsPage';
import { DEFAULT_RETURN_POLICY_HTML } from '@c/modals/ReturnPolicyModal';
import SellerProfileHeader from '@c/headers/sellerprofile/SellerProfileHeader';
import FollowSellerButton from '@c/buttons/FollowSellerButton';
import FirstVisitFollowPopup from '@c/FirstVisitFollowPopup';
import {
  FlagIcon,
  GlobeSimpleIcon,
  MessageIcon,
  NoteIcon,
  ShareAltIcon,
  StarIcon,
  ShieldCheckIcon,
} from '@c/icons';
import { DefaultLayout } from '@c/layouts';
import { addCacheTag } from '@vercel/functions';
import ChargeOrAddFundsModal from '@c/modals/ChargeOrAddFundsModal';
import ReportItemModal from '@c/modals/ReportItemModal';
import ShareModal from '@c/modals/ShareModal';
import ProductsPage from '@c/ProductsPage';
import { useQuery } from '@tanstack/react-query';
import Chip from '@ui/Chip';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import ButtonToggle from '@ui/ButtonToggle';
import SafeImage from '@ui/SafeImage';
import Tabs, { TabItem } from '@ui/Tabs';
import Toggle from '@ui/Toggle';
import { CategoryDocument } from '@util/firestore/categories';
import { ConditionDocument } from '@util/types/firestore/conditions';
import {
  getAllAuctionsBySellerId,
  getProducts,
  setVacationMode,
} from '@util/firestore/products/';
import { getReviewsByUid } from '@util/firestore/reviews';
import { getRedirectBySlug } from '@util/firestore/redirects'
import {
  getUserById,
  getUserByUsernameSlug,
  getUserToken,
  setUserPermissionFlags,
  updateAuthUser,
  updateUserByKey,
  updateUsername,
} from '@util/firestore/users';
import { getProductQueryArgsFromContext } from '@util/getPropsFromConext';
import { useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { ListingsFilters, PaginationFilters } from '@util/hooks/useProductsPage';
import { ButtonToggleType } from '@util/types/buttontoggle';
import { ProductDocument } from '@util/types/firestore/products';
import { ReviewDocument } from '@util/types/firestore/review';
import { UserDocument, noteSchema } from '@util/types/firestore/users';
import { z } from 'zod';

type Note = z.infer<typeof noteSchema>;
import { Pagination } from '@util/types/paginator';
import { getProductUrl } from '@util/urlHelpers';
import { ArcElement, Chart as ChartJS, Tooltip } from 'chart.js';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';

import { getReturnPolicies, getReturnPolicy, PolicyDocument } from '@util/firestore/settings';
import { BrandDocument } from '@util/types/firestore/brand';
import { getActiveListingsCount, getSoldListingsCount } from '@util/firestore/listing';
import { toSerializable } from '@util/serializeFirestore';
import { toPublicStorefrontUser, withoutShipFrom } from '@util/firestore/users/publicStorefrontUser';
ChartJS.register(ArcElement, Tooltip);

interface SellerProfileProps {
  user: UserDocument;
  sellerLocation?: string;
  reviews?: ReviewDocument[];
  auctionProducts?: ProductDocument[];
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: PaginationFilters;
  initialSideFilters: ListingsFilters;
  initialTab?: 'listings' | 'auctions' | 'reviews' | 'info' | 'notes';
  useClient?: boolean;
  brandOptions: BrandDocument[];
  categoryOptions: CategoryDocument[];
  conditionOptions: ConditionDocument[];
}

const SellerProfileListings = ({
  user,
  initialProducts = [],
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  useClient, 
  brandOptions,
  categoryOptions,
  conditionOptions,
}: SellerProfileProps) => {
  return (
    <ProductsPage
      initialProducts={initialProducts}
      initialPageInfo={initialPageInfo}
      initialTopFilters={initialTopFilters}
      initialSideFilters={initialSideFilters}
      headerBreadcrumb={[
        { label: 'Home', href: '/' },
        { label: user.username, href: `/shop-seller/${user.username}` }
      ]}
      pageType="all"
      keyword={null}
      categoryOptions={categoryOptions}
      conditionOptions={conditionOptions}
      brandOptions={brandOptions}
      useClient={useClient}
      seller_id={user.uid}
      slugToTitle={undefined}
    />
  );
};

const ReviewCard = ({ review }: { review: ReviewDocument }) => {
  const { data: user } = useQuery(['user', review.reviewer_id], () =>
    getUserById(review.reviewer_id)
  );

  return (
    <div className="flex gap-[1.6rem]">
      {review.products?.map((product) => {
        return product.thumbnail ? (
          product.slug ? (
            <Link href={getProductUrl(product.slug)} key={product.slug}>
              <SafeImage
                alt=""
                src={product.thumbnail}
                height={100}
                width={100}
                className="hover:scale-105"
              />
            </Link>
          ) : (
            <div className="min-w-[100px] max-w-[100px]">
                <SafeImage
                  key={product.thumbnail}
                  alt=""
                  src={product.thumbnail}
                  height={100}
                  width={100}
                />
            </div>
            
          )
        ) : (
          <div className="min-h-[4.4rem] min-w-[4.4rem]" key={product.id}>
            <Avatar user={user} />
          </div>
        );
      })}

      <div className="flex grow flex-col gap-[0.5rem]">
        <div className="flex grow justify-between">
          
          <span>
            {review.created_at &&
              new Date(review.created_at).toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
          </span>
        </div>
        <div className="flex gap-[.5rem]">
          {/* render a star icon for the value of the rating. review.rating === 1 means render 1 star */}
          {[...Array(review.rating)].map((_, index) => (
            <StarIcon key={index} />
          ))}
          {/* render a star with a fill of #808080 for 5 - review.rating */}
          {[...Array(5 - review.rating)].map((_, index) => (
            <StarIcon key={index} fill="#808080" />
          ))}
        </div>
        <p className="line-clamp-3 font-semibold">
          {review.products?.map((product) => product.title).join(', ')}
        </p>
        <p>{review.comment}</p>
      </div>
    </div>
  );
};

const SellerProfileReviews = ({
  reviews,
  user,
}: {
  reviews: ReviewDocument[];
  user: UserDocument;
}) => {
  // viewMode filters by what role the subject (person being reviewed) had
  const [viewMode, setViewMode] = useState<'buyer' | 'seller'>('seller');
  const [ratingFilter, setRatingFilter] = useState<'all' | 'positive' | 'negative'>('all');

  // Filter reviews by subject_role (what role did this user have in the transaction)
  // For legacy reviews without subject_role, default to 'seller' since historically
  // most reviews on profiles were of sellers (buyers reviewing sellers)
  const roleFilteredReviews = reviews.filter((review) => {
    const subjectRole = review.subject_role || 'seller'; // Legacy reviews default to seller
    return subjectRole === viewMode;
  });

  // Then apply positive/negative filter
  const positiveReviews = roleFilteredReviews.filter((review) => review.rating >= 3);
  const negativeReviews = roleFilteredReviews.filter((review) => review.rating < 3);

  const displayedReviews = ratingFilter === 'all'
    ? roleFilteredReviews
    : ratingFilter === 'positive'
      ? positiveReviews
      : negativeReviews;

  const buttons: ButtonToggleType[] = [
    {
      label: 'All',
      onClick: () => setRatingFilter('all'),
      key: 'all',
      selected: ratingFilter === 'all',
    },
    {
      label: 'Positive',
      onClick: () => setRatingFilter('positive'),
      key: 'positive',
      selected: ratingFilter === 'positive',
    },
    {
      label: 'Negative',
      onClick: () => setRatingFilter('negative'),
      key: 'negative',
      selected: ratingFilter === 'negative',
    },
  ];

  const Data = [
    {
      label: 'Positive',
      count: positiveReviews.length,
    },
    {
      label: 'Negative',
      count: negativeReviews.length,
    },
  ];

  const chartData = {
    labels: Data.map((data) => data.label),
    datasets: [
      {
        data: Data.map((data) => data.count),
        backgroundColor: ['#C75008', '#808080'],
        borderWidth: 0,
      },
    ],
  };

  return (
    <>
      {reviews.length > 0 ? (
        <div className="flex max-w-[100vw] flex-col-reverse gap-[12rem] px-4 py-5 sm:flex-row">
          <div className="flex w-full flex-col gap-[3rem]">
            <div className="flex grow flex-col gap-[1.6rem] sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-[1.6rem]">
                <span className="text-[1.8rem] font-semibold">
                  {roleFilteredReviews.length} Reviews
                </span>
                <div className="flex rounded-[0.8rem] border-[1px] p-[0.4rem]">
                  <button
                    className={`rounded-[0.6rem] px-[1.6rem] py-[0.8rem] text-[1.4rem] font-medium transition-colors ${
                      viewMode === 'seller'
                        ? 'bg-brand-secondary text-white'
                        : 'text-brand-gray hover:bg-gray-100'
                    }`}
                    onClick={() => setViewMode('seller')}
                  >
                    As Seller
                  </button>
                  <button
                    className={`rounded-[0.6rem] px-[1.6rem] py-[0.8rem] text-[1.4rem] font-medium transition-colors ${
                      viewMode === 'buyer'
                        ? 'bg-brand-secondary text-white'
                        : 'text-brand-gray hover:bg-gray-100'
                    }`}
                    onClick={() => setViewMode('buyer')}
                  >
                    As Buyer
                  </button>
                </div>
              </div>
              <ButtonToggle buttons={buttons} />
            </div>
            <div className="flex flex-col gap-[6rem] p-[2.4rem]">
              {displayedReviews.map((review, index) => (
                <ReviewCard review={review} key={index} />
              ))}
            </div>
          </div>
          <DoughnutChart chartData={chartData} heading={'Reviews Summary'} />
        </div>
      ) : user.reviews > 0 ? (
        <div className="mt-[5rem]">
          <ReviewSummary
            userDoc={user}
            positiveReviews={positiveReviews.length}
            negativeReviews={negativeReviews.length}
          />
        </div>
      ) : (
        <div className="mt-[5rem]">
          <EmptyState
            icon={<NoteIcon />}
            title={'No Reviews Yet!'}
            description={'Sell some items to get reviews.'}
          />
        </div>
      )}
    </>
  );
};

const SellerProfileInfo = ({
  user,
  sellerLocation,
}: {
  user: UserDocument;
  sellerLocation?: string;
}) => {
  const [returnPolicy, setReturnPolicy] = useState<PolicyDocument | null>();

  const Label = ({ label }: { label: string }) => (
    <p className="text-[1.5rem] font-semibold sm:min-w-[28.2rem]">{label}</p>
  );

  useEffect(() => {
    const fetchReturnPolicy = async () => {
      try {
        let policy: null | PolicyDocument | PolicyDocument[] = null;
        if (user.return_policy) {
          policy = await getReturnPolicy(user.return_policy!);
        }
        if (!policy) {
          const policies = await getReturnPolicies(true);
          policy = policies[0];
        }
        if (policy) setReturnPolicy(policy as PolicyDocument);
      } catch (error) {
        console.error('Error fetching return policy:', error);
      }
    };
    fetchReturnPolicy();
  }, [user]);


  return (
    <div className="flex grow flex-col gap-[2.4rem] px-4 py-10 text-[1.5rem]">
      <div className="flex flex-col justify-between sm:flex-row">
        <Label label="Joined" />
        <div className="grow">
          {new Date(user.created).toLocaleString('default', {
            month: 'long',
            day: '2-digit',
            year: 'numeric',
          })}
        </div>
      </div>
      <div className="flex flex-col justify-between sm:flex-row">
        <Label label="About" />
        <div className="grow">
          {user.bio ? user.bio : 'No bio provided yet.'}
        </div>
      </div>
      <div className="flex flex-col justify-between sm:flex-row">
        <Label label="Location" />
        {sellerLocation ? (
          <div className="grow">{sellerLocation}</div>
        ) : (
          <div className="grow">No location provided yet.</div>
        )}
      </div>
      <div className="flex flex-col justify-between sm:flex-row">
        <Label label="Shipping Info" />
        <div
          className="grow"
          dangerouslySetInnerHTML={{
            __html: user.shipping_policy ?? DEFAULT_SHIPPING_POLICY,
          }}
        ></div>
      </div>
      <div className="flex flex-col justify-between sm:flex-row">
        <Label label="Return Policy" />
        <div
          className="grow"
          dangerouslySetInnerHTML={{
            __html: returnPolicy?.html || DEFAULT_RETURN_POLICY_HTML,
          }}
        ></div>
      </div>
    </div>
  );
};

const SellerProfileNotes = ({
  user,
  currentUserDoc,
}: {
  user: UserDocument;
  currentUserDoc: UserDocument | null;
}) => {
  const [notes, setNotes] = useState<Note[]>(user.notes || []);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const addNote = async () => {
    if (!newNoteContent.trim()) {
      setSaveMessage('Please enter some content');
      return;
    }

    if (!currentUserDoc?.uid) {
      setSaveMessage('You must be logged in');
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    const newNote: Note = {
      created: Date.now(),
      value: newNoteContent,
      uid: currentUserDoc.uid,
    };

    const updatedNotes = [...notes, newNote];

    try {
      await updateUserByKey(user.uid, 'notes', updatedNotes);
      setNotes(updatedNotes);
      setNewNoteContent('');
      setSaveMessage('Note added successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving note:', error);
      setSaveMessage('Error saving note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Sort notes by created date, newest first
  const sortedNotes = [...notes].sort((a, b) => b.created - a.created);

  return (
    <div className="flex flex-col gap-[2.4rem] px-4 py-10 max-w-[80rem]">
      {/* Add New Note Section */}
      <div className="rounded-[10px] border border-brand-lightest-gray p-[2.4rem]">
        <h3 className="mb-[1.6rem] text-[1.8rem] font-semibold">Add Note</h3>
        <p className="mb-[1.2rem] text-[1.4rem] text-brand-light-black">
          Track verification status, admin actions, or important account information.
        </p>
        <textarea
          className="mb-[1.6rem] block min-h-[15rem] w-full resize-none rounded-[10px] bg-brand-darker-white p-[1.6rem] text-[1.4rem] focus:outline-brand-primary focus:outline-2"
          placeholder="Enter verification notes, status updates, or other admin information..."
          value={newNoteContent}
          onChange={(e) => setNewNoteContent(e.target.value)}
        />
        <div className="flex items-center gap-[1.6rem]">
          <Button
            type="primary"
            text={isSaving ? 'Saving...' : 'Add Note'}
            onClick={addNote}
            disabled={isSaving || !newNoteContent.trim()}
            height="small"
          />
          {saveMessage && (
            <span className={`text-[1.4rem] ${saveMessage.includes('Error') || saveMessage.includes('Please') ? 'text-brand-red' : 'text-green-600'}`}>
              {saveMessage}
            </span>
          )}
        </div>
      </div>

      {/* Existing Notes Section */}
      <div>
        <h3 className="mb-[1.6rem] text-[1.8rem] font-semibold">
          Notes History ({sortedNotes.length})
        </h3>
        {sortedNotes.length === 0 ? (
          <div className="flex flex-col items-center rounded-[10px] bg-brand-darker-white p-[3.2rem] text-center text-brand-light-black">
            <NoteIcon />
            <p className="mt-[1.2rem] text-[1.4rem]">No notes yet. Add verification data or status updates above.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-[1.6rem]">
            {sortedNotes.map((note, index) => (
              <div
                key={`${note.created}-${index}`}
                className="rounded-[10px] border border-brand-lightest-gray bg-brand-white p-[1.6rem]"
              >
                <div className="mb-[1.2rem] flex items-center justify-between text-[1.2rem] text-brand-light-black">
                  <span className="font-medium">
                    {new Date(note.created).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span className="rounded bg-brand-darker-white px-[0.8rem] py-[0.4rem] font-mono text-[1.1rem]">
                    {note.uid.slice(0, 8)}...
                  </span>
                </div>
                <div className="whitespace-pre-wrap text-[1.4rem] leading-relaxed">
                  {note.value}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SellerProfile = ({
  user,
  reviews,
  initialTab,
  initialProducts,
  auctionProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  sellerLocation,
  categoryOptions,
  conditionOptions,
  brandOptions,
}: SellerProfileProps) => {
  const router = useRouter();

  const { userDoc, signInWithToken } = useAuth();
  // GB web#1469: SSR `user` is now the public-safe subset (no `notes`,
  // Stripe ids, or full permissions/roles maps — see
  // publicStorefrontUser.ts). Admin-only UI that needs the full doc —
  // the Notes tab below, and the admin overlay's Stripe links/permission
  // buttons further down — reads `adminSellerDoc` instead. Declared here,
  // before `tabs`, because the Notes tab's `contents` depends on it.
  // Load the full seller document only for authenticated admin controls.
  // Public SSR props keep the storefront allowlist.
  const {
    data: adminSellerDoc,
    isError: adminSellerDocIsError,
    isSuccess: adminSellerDocIsSuccess,
  } = useQuery(
    ['adminSellerDoc', user.uid],
    () => getUserById(user.uid),
    { enabled: !!userDoc?.roles?.admin },
  );
  const tabs: TabItem[] = [
    {
      title: 'Listings',
      contents: (
        <SellerProfileListings
          user={user}
          initialProducts={initialProducts}
          initialPageInfo={initialPageInfo}
          initialTopFilters={initialTopFilters}
          initialSideFilters={initialSideFilters}
          categoryOptions={categoryOptions}
          conditionOptions={conditionOptions}
          brandOptions={brandOptions}
        />
      ),
    },
    {
      title: 'Auctions',
      hidden: !user?.roles?.auctions,
      contents: (
        <SellerProfileListings
          user={user}
          initialProducts={auctionProducts ?? []}
          initialPageInfo={initialPageInfo}
          initialTopFilters={initialTopFilters}
          initialSideFilters={initialSideFilters}
          categoryOptions={categoryOptions}
          conditionOptions={conditionOptions}
          brandOptions={brandOptions}
          useClient
        />
      ),
    },
    {
      title: 'Reviews',
      contents: <SellerProfileReviews reviews={reviews ?? []} user={user} />,
    },
    {
      title: 'Info',
      contents: (
        <SellerProfileInfo user={user} sellerLocation={sellerLocation} />
      ),
    },
    {
      title: 'Notes',
      hidden: !userDoc?.roles?.admin,
      // GB web#1469: `notes` is admin-only data, not in the public SSR
      // `user`. Render from `adminSellerDoc` (the full doc) instead, and
      // show a loading placeholder until it arrives — SellerProfileNotes
      // seeds its `notes` state from `user.notes` only on mount, so it
      // must not mount against a doc that has no `notes` field yet.
      // `key={adminSellerDoc.uid}` remounts the component when the viewed
      // seller changes, so its `notes` state re-seeds instead of carrying
      // over the previous seller's notes. When the query errors, show a
      // retry message instead of leaving the loading text up forever.
      contents: adminSellerDoc ? (
        <SellerProfileNotes
          key={adminSellerDoc.uid}
          user={adminSellerDoc}
          currentUserDoc={userDoc}
        />
      ) : adminSellerDocIsError ? (
        <div className="px-4 py-10 text-[1.4rem] text-brand-light-black">
          Couldn&apos;t load notes. Reload the page to try again.
        </div>
      ) : adminSellerDocIsSuccess ? (
        // The fetch finished but found no seller doc — say so rather than
        // showing "Loading notes…" forever.
        <div className="px-4 py-10 text-[1.4rem] text-brand-light-black">
          No seller record found, so there are no notes to show.
        </div>
      ) : (
        <div className="px-4 py-10 text-[1.4rem] text-brand-light-black">
          Loading notes…
        </div>
      ),
    },
  ];
  const [openModal, setOpenModal] = useState<
    'returnPolicy' | 'reportItem' | 'share' | 'offer' | 'chargeOrAdd' | ''
  >('');
  const [chargeOrAdd, setChargeOrAdd] = useState<'charge' | 'add'>('charge');

  const { openChatModalWithRecipientAndProductId } = useChat();
  const { StatusLabel } = useOnlineStatus(user.uid);
  // adminSellerDoc is fetched above (before `tabs`); the admin overlay
  // below reads Stripe ids + permission flags via `adminUser`, falling
  // back to the public `user` (those fields are simply absent) until it
  // loads.
  const adminUser = adminSellerDoc ?? user;
  const [loadingBtn, setLoadingBtn] = useState('');
  const [soldListings, setSoldListings] = useState<number>()
  const [liveListings, setLiveListings] = useState<number>()

  useEffect(() => {
    const calculateMetrics = async () => {
      // For Refocus Store, use product-based count with multiplier (special case)
      // For all other users, use actual sales count from user.metrics
      let soldCount: number;
      if (user.integrations?.source === "refocus") {
        // Refocus Store: Keep existing behavior - count products with sales, apply multiplier
        const productSoldCount = await getSoldListingsCount(user.uid);
        soldCount = Math.round(productSoldCount * (user.integrations.sales_offset || 0.1));
      } else {
        // All other users: Use actual num_sold from order history
        soldCount = user.metrics?.num_sold ?? 0;
      }
      setSoldListings(soldCount)
      const liveCount = await getActiveListingsCount(user.uid)
      setLiveListings(liveCount)
    }
    calculateMetrics()
  }, [user.uid, user.metrics?.num_sold, user.integrations?.source])
  const impersonate = () => {
    setLoadingBtn('impersonate');
    getUserToken('', user.uid)
      .then((token) => {
        if (token) signInWithToken({ email: token, password: '' });
        else alert('User not found');
      })
      .finally(() => setLoadingBtn(''));
  };

  const changeUsername = async () => {
    const input = prompt('Enter new username');
    if (input) {
      setLoadingBtn('changeUsername');
      await updateUsername(user.uid, input);
    }
    router.reload();
  };

  const changeEmail = async () => {
    const input = prompt('Enter new email');
    if (input) {
      try {
        setLoadingBtn('changeEmail');
        await updateAuthUser(user.uid, undefined, undefined, input);
        await updateUserByKey(user.uid, 'email', input);
        router.reload();
      } catch (e) {
        alert((e as Error)?.message || e);
        setLoadingBtn('');
      }
    }
  };

  // GB web#1469: these four admin handlers write `permissions` via
  // setUserPermissionFlags — a single-field dot-path update — instead of
  // updateUserDoc({permissions: {...}}), which replaces the whole map and
  // could wipe or race a concurrent admin's write. See
  // setUserPermissionFlags in user.service.ts.
  const verifyEmail = async () => {
    try {
      setLoadingBtn('verifyEmail');
      await updateAuthUser(user.uid, true);
      await setUserPermissionFlags(user.uid, { is_email_verified: true });
      router.reload();
    } catch (e) {
      alert((e as Error)?.message || e);
      setLoadingBtn('');
    }
  };

  const toggleVacation = async () => {
    try {
      const text = user.on_vacation
        ? 'Are you sure you want to turn off vacation mode? This will make all listings visible again.'
        : 'Are you sure you want to turn on vacation mode? This will temporarily remove all listings until turned back on.';
      const yes = confirm(text);
      if (yes) {
        setLoadingBtn('toggleVacation');
        await setVacationMode(user.uid, !user.on_vacation);
        router.reload();
      }
    } catch (e) {
      alert((e as Error)?.message || e);
    }
  };

  const setBan = async (banned: boolean) => {
    try {
      setLoadingBtn('setBan');
      await updateAuthUser(user.uid, undefined, banned);
      await setUserPermissionFlags(user.uid, { is_banned: banned });
      router.reload();
    } catch (e) {
      alert((e as Error)?.message || e);
    }
  };

  const setFraud = async (fraud: boolean) => {
    try {
      setLoadingBtn('setFraud');
      await updateAuthUser(user.uid, undefined, fraud);
      await setUserPermissionFlags(user.uid, { is_fraudulent: fraud });
      router.reload();
    } catch (e) {
      alert((e as Error)?.message || e);
    }
  };

  const setVerification = async (verify: boolean) => {
    try {
      setLoadingBtn('setVerification');
      await setUserPermissionFlags(user.uid, { is_verified: verify });
      router.reload();
    } catch (e) {
      alert((e as Error)?.message || e);
    }
  };

  const badgeText = user.badges && user.badges[user.badges.length - 1];
  const colorOptions = {
    'New User': 'pending',
    'Verified User': 'primary',
    'First-Time Buyer': 'success',
    'Frequent Buyer': 'active',
    'New Seller': 'success',
    'Rising Seller': 'active',
    'Top Seller': 'success',
  };
  const badgeColor = (colorOptions as Record<string, string>)[badgeText] || 'primary';

  return (
    <>
      {/* GB #691: first-visit one-time Follow nudge. Self-gates (shown once ever,
          never on the seller's own page); fixed-position so tree placement is
          cosmetic. */}
      <FirstVisitFollowPopup sellerId={user.uid} sellerName={user.username} />
      <SellerProfileHeader seller={user} />
      <div className="
        w-full
        max-w-[95%]
        lg:max-w-[120rem]
        flex flex-col z-10
        -mt-[6rem]
        rounded-t-[1rem] 
        bg-brand-white
        overflow-x-hidden
      ">
        <div className="flex flex-col items-center justify-between p-[2.4rem] sm:flex-row">
          <div className="flex gap-[1.6rem]">
            <div className="hidden grow md:block">
              <Avatar user={user} size="large" />
            </div>
            <div className="md:hidden">
              <Avatar user={user} size="medium" />
            </div>
            <div className="flex flex-col justify-center gap-[1.6rem]">
              <div className="flex flex-wrap items-center gap-[1.4rem] sm:flex-nowrap">
                <h1 className="text-[2.4rem] font-semibold leading-[2.0rem]">
                  {user.username}
                </h1>
                <div className="hidden md:flex">
                  <StatusLabel />
                </div>
              </div>
              <div className="flex items-center gap-[1.5rem]">
                {user.country && (
                  <div className="flex gap-[0.3rem]">
                    <GlobeSimpleIcon />
                    <span className="text-[1.3rem] font-normal leading-normal text-brand-light-black">
                      {user.country}
                    </span>
                  </div>
                )}
                {user?.permissions?.is_verified && (
                  <div className="flex flex-wrap justify-center gap-[0.6rem]">
                    <span><ShieldCheckIcon width={16} height={18} fill="rgb(36 46 95)" /></span>
                    <span className="flex text-[1.0rem]">
                      Verified<span className="hidden sm:flex">&nbsp;By GearFocus</span>
                    </span>
                  </div>
                )}
                {user?.badges && (
                  <Chip
                    size="small"
                    text={badgeText}
                    color={badgeColor as any}
                  />
                )}           
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-[3rem] sm:gap-[5rem] sm:flex-row">
            <div className="flex justify-evenly gap-[2rem]">
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-[0.8rem] text-[2.4rem] font-semibold">
                  <StarIcon />
                  {(user.rating ?? 0).toFixed(1)}
                </div>
                <div className="text-[1.3rem]">{user.reviews ?? 0} Reviews</div>
              </div>
              <div className="self-stretch w-[0.1rem] bg-brand-lightest-gray" />
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-[0.8rem] text-[2.4rem] font-semibold">
                  {typeof liveListings === 'number' ? liveListings : (
                    <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-lg mb-[0.6rem]">
                      <div className="h-full w-full animate-pulse bg-gray-200" />
                    </div>
                  )}
                </div>
                <div className="text-[1.3rem]">Listings</div>
              </div>
              <div className="self-stretch w-[0.1rem] bg-brand-lightest-gray" />
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-[0.8rem] text-[2.4rem] font-semibold">
                  {typeof soldListings === 'number' ? soldListings : (
                    <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-lg mb-[0.6rem]">
                      <div className="h-full w-full animate-pulse bg-gray-200" />
                    </div>
                  )}
                </div>
                <div className="text-[1.3rem]">Sales</div>
              </div>
            </div>

            {/* gap was `gap-[rem]` — not a valid Tailwind value, so it
                compiled to `gap: rem` and was dropped, leaving the row with no
                spacing at all. Message and Follow are peer actions, so they use
                the same equal-width/gap pairing as the offer action row in
                MessageOffer.tsx: width="fixed" (w-[15rem]) + gap-[1.6rem]. */}
            <div className="flex flex-wrap justify-center gap-[1.6rem] sm:flex-nowrap">
              <Button
                type="secondary"
                text="Message"
                width="fixed"
                leadingIcon={<MessageIcon />}
                disabled={!userDoc || user.uid === userDoc.uid}
                onClick={() => {
                  if (userDoc && user.uid !== userDoc.uid) {
                    openChatModalWithRecipientAndProductId({
                      recipientUid: user.uid,
                    });
                  }
                }}
              />
              <FollowSellerButton sellerId={user.uid} />
              <div className="flex-wrap justify-center sm:flex-nowrap hidden md:flex">
                <Button
                  type="text"
                  width="small"
                  leadingIcon={<ShareAltIcon />}
                  noXPadding
                  onClick={() => setOpenModal('share')}
                />
                <Button
                  type="text"
                  leadingIcon={<FlagIcon />}
                  width="small"
                  noXPadding
                  onClick={() => setOpenModal('reportItem')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* GB web#1469: the SSR `user` is the public subset, so it has no
            ban/fraud/email-verified flags or Stripe ids. Render the admin
            controls only from the full admin-fetched doc — never from the
            public fallback, which would show every seller as un-banned,
            un-flagged, and without Stripe links. */}
        {!!userDoc?.roles?.admin && !adminSellerDoc && (
          <div
            className="hidden md:flex justify-center text-[1.4rem] text-brand-light-black"
            style={{ zoom: 0.7 }}
          >
            {adminSellerDocIsError
              ? 'Couldn\u2019t load admin controls. Reload the page to try again.'
              : adminSellerDocIsSuccess
                ? 'No seller record found, so admin controls are unavailable.'
                : 'Loading admin controls\u2026'}
          </div>
        )}
        {!!userDoc?.roles?.admin && !!adminSellerDoc && (
          <div
            className="hidden md:flex flex-wrap items-center justify-center gap-5"
            style={{ zoom: 0.7 }}
          >
            {((!!user.metrics?.live_listings && !user.on_vacation) ||
              user.on_vacation) && (
              <div className="flex items-center gap-4">
                <Toggle enabled={user.on_vacation} onChange={toggleVacation} />
                <h4>Vacation Mode</h4>
              </div>
            )}
            {!adminUser.permissions?.is_verified ? (
              <Button
                loading={loadingBtn === 'setVerification'}
                height="small"
                type="tertiary"
                width="small"
                text="Verify Account"
                onClick={() => setVerification(true)}
              />
            ) : (
              <Button
                loading={loadingBtn === 'setVerification'}
                height="small"
                type="tertiary"
                width="small"
                text="Unverify Account"
                onClick={() => setVerification(false)}
              />
            )}
            {!!adminUser.customer_id && (
              <Button
                height="small"
                type="tertiary"
                width="small"
                text="Stripe Customer"
                href={`https://dashboard.stripe.com/customers/${adminUser.customer_id}`}
                target="_blank"
              />
            )}
            {!!adminUser.account_id && (
              <Button
                height="small"
                type="tertiary"
                width="small"
                text="Stripe Seller"
                href={`https://dashboard.stripe.com/connect/accounts/${adminUser.account_id}`}
                target="_blank"
              />
            )}
            <Button
              height="small"
              type="tertiary"
              width="small"
              text="Backend"
              href={`https://console.firebase.google.com/project/gear-focus/firestore/data/~2Fusers~2F${user.uid}`}
              target="_blank"
            />
            <Button
              height="small"
              type="tertiary"
              width="small"
              text="View Purchases"
              href={`/dashboard/purchases?uid=${user.uid}`}
            />
            <Button
              height="small"
              type="tertiary"
              width="small"
              text="View Orders"
              href={`/dashboard/orders?uid=${user.uid}`}
            />
            <Button
              loading={loadingBtn === 'impersonate'}
              height="small"
              type="tertiary"
              width="small"
              text="Impersonate"
              onClick={impersonate}
            />
            <Button
              loading={loadingBtn === 'changeUsername'}
              height="small"
              type="tertiary"
              width="small"
              text="Change Username"
              onClick={changeUsername}
            />
            <Button
              loading={loadingBtn === 'changeEmail'}
              height="small"
              type="tertiary"
              width="small"
              text="Change Email"
              onClick={changeEmail}
            />
            {!adminUser.permissions?.is_email_verified && (
              <Button
                loading={loadingBtn === 'verifyEmail'}
                height="small"
                type="tertiary"
                width="small"
                text="Verify Email"
                onClick={verifyEmail}
              />
            )}
            {!!adminUser.account_id && (
              <>
                <Button
                  height="small"
                  type="tertiary"
                  width="small"
                  text="Charge Seller"
                  onClick={() => {
                    setChargeOrAdd('charge');
                    setOpenModal('chargeOrAdd');
                  }}
                />
                <ChargeOrAddFundsModal
                  variant={chargeOrAdd}
                  isOpen={openModal === 'chargeOrAdd'}
                  dismiss={() => setOpenModal('')}
                  seller={adminUser}
                />
                <Button
                  onClick={() => {
                    setChargeOrAdd('add');
                    setOpenModal('chargeOrAdd');
                  }}
                  height="small"
                  type="tertiary"
                  width="small"
                  text="Add Funds"
                />
              </>
            )}
            {adminUser.permissions?.is_banned ? (
              <Button
                loading={loadingBtn === 'setBan'}
                height="small"
                type="error"
                width="small"
                text="Unban User"
                onClick={() => setBan(false)}
              />
            ) : (
              <Button
                loading={loadingBtn === 'setBan'}
                height="small"
                type="tertiary"
                width="small"
                text="Ban User"
                onClick={() => setBan(true)}
              />
            )}
            {!adminUser.permissions?.is_fraudulent ? (
              <Button
                loading={loadingBtn === 'setFraud'}
                height="small"
                type="tertiary"
                width="small"
                text="Mark As Fraud"
                onClick={() => setFraud(true)}
              />
            ) : (
              <Button
                loading={loadingBtn === 'setFraud'}
                height="small"
                type="error"
                width="small"
                text="Unmark As Fraud"
                className="text-red"
                onClick={() => setFraud(false)}
              />
            )}
          </div>
        )}

        <Tabs
          tabItems={tabs}
          initialTab={tabs.findIndex(
            (tab) => tab.title.toLowerCase() === initialTab
          )}
        />

      </div>

      <ShareModal
        isOpen={openModal === 'share'}
        title={user.username}
        thumb={user.photo}
        type="user"
        dismiss={() => setOpenModal('')}
      />
      <ReportItemModal
        isUser
        itemId={user.uid}
        isOpen={openModal === 'reportItem'}
        dismiss={() => setOpenModal('')}
      />
    </>
  );
};

SellerProfile.getLayout = (page: React.ReactNode) => {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export const getServerSideProps: GetServerSideProps<
  SellerProfileProps
> = async (context) => {
  if (context.params?.username) {
    const { username } = context.params;
    
    const user = await getUserByUsernameSlug(username + '');

    if (!user) {
      const { results: redirects } = await getRedirectBySlug({ slug: `/shop-sellers/${username}` });

      if (redirects) {
        return {
          redirect: {
            destination: redirects.destination,
            permanent: redirects.permanent
          }
        }
      } else {
        return {
          redirect: {
            destination: '/products',
            permanent: true
          }
        }
      }
    }

    if (user) {
      const reviews = await getReviewsByUid(user.uid);
      const args = await getProductQueryArgsFromContext(context);
      args.seller_id = user.uid;
      const response = await getProducts(args);
      const shopAddress =
        user.addresses?.find((address) => address.is_shop_location) ??
        user.addresses?.[0] ??
        null;
      // hides seller's full address
      const sellerLocation = shopAddress
        ? `${shopAddress.city_locality}, ${shopAddress.country_code}`
        : user.country;

      let auctionProducts: ProductDocument[] = [];
      if (user.roles?.auctions) {
        const res = await getAllAuctionsBySellerId(user.uid);
        auctionProducts = res.results.sort(
          (a, b) => b.date_added - a.date_added
        );
      }
      const initialTab = context.query.tab as
        | 'listings'
        | 'auctions'
        | 'info'
        | 'reviews'
        | 'notes'
        | undefined;

      // Add cache headers for performance
      context.res.setHeader(
        'Cache-Control',
        'public, max-age=0, s-maxage=60, stale-while-revalidate=300'
      );

      // Add cache tags for on-demand invalidation
      addCacheTag(`seller-${user.uid}`);
      addCacheTag('sellers');

      return {
        // Products, user and reviews are raw Firestore docs here (getProducts
        // falls back to Firestore when a query can't use Algolia), so any
        // Timestamp on them would fail serialization.
        props: toSerializable({
          categoryOptions: args.categoryOptions,
          conditionOptions: args.conditionOptions,
          brandOptions: args.brandOptions,
          // GB web#1469: allowlisted subset only — the raw Firestore user
          // doc must never reach __NEXT_DATA__ (see publicStorefrontUser.ts).
          // Everything above this point (sellerLocation, auctionProducts,
          // metaTags/title below) still reads the FULL server-side `user`.
          user: toPublicStorefrontUser(user),
          sellerLocation,
          // GB web#1469: `shipment.ship_from` carries the seller's name,
          // phone, and full street address — strip it from every product
          // reaching props (listings, auctions, and the products embedded in
          // each review) while keeping `shipment.package` and `account_id`.
          // See withoutShipFrom in publicStorefrontUser.ts.
          reviews: reviews.map((review) =>
            review.products
              ? { ...review, products: review.products.map(withoutShipFrom) }
              : review
          ),
          auctionProducts: auctionProducts.map(withoutShipFrom),
          initialTab: initialTab ?? 'listings',
          initialProducts: response.results.map(withoutShipFrom),
          initialPageInfo: response.pageInfo,
          initialTopFilters: {
            sort: args.sort,
            limit: args.limit,
            view: (context.query.view as 'list') || 'grid',
          },
          initialSideFilters: {
            minPrice: args.minPrice,
            maxPrice: args.maxPrice,
            condition: args.conditionFilters,
            category: args.categoryFilters,
            brand: args.brandFilters,
            deals: args.deals,
            useCases: args.useCases,
            additional: args.additionalFilters
          },
          title: `${user.username} | GearFocus`,
          metaTags: [
            {
              key: 'og:title',
              property: 'og:title',
              content: `${user.username} | GearFocus`,
            },
            {
              key: 'og:description',
              property: 'og:description',
              content:
                user.bio ||
                "A seller on GearFocus, the world's largest marketplace for used photography gear.",
            },
            {
              key: 'og:image',
              property: 'og:image',
              content: user.store_photo ?? '/shop-seller-header.png',
            },
            {
              key: 'twitter:image',
              attributes: { name: 'twitter:image' },
              content: user.store_photo ?? '/shop-seller-header.png',
            },
            {
              key: 'twitter:title',
              attributes: { name: 'twitter:title' },
              content: `${user.username} | GearFocus`,
            },
          ],
        }),
      };
    }
  }
  return { notFound: true };
};

export default SellerProfile;
